/** @jsxFrag React.Fragment */

/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedHTMLMessage } from 'js/lib/coursera.react-intl';

import { Typography2 } from '@coursera/cds-core';

import courseraPlusEpicClient from 'bundles/epic/clients/courseraPlus';
import { RegionalPromotionFullWidthBanner } from 'bundles/front-page/components/banners/regional-promotion-banner/RegionalPromotionFullWidthBanner';
import ReactPriceDisplay from 'bundles/payments-common/components/ReactPriceDisplay';

import _t from 'i18n!nls/front-page';

const styles = {
  link: css`
    text-decoration: underline;
  `,
};

type Props = {
  hideGlobalPromotion?: boolean;
  variant?: 'blue' | 'purple' | 'light-blue';
};

export const shouldRenderRegionalPromotionBanner = ({ hideGlobalPromotion }: Props) => {
  const epicValues = [
    courseraPlusEpicClient.get('regionalPromotionQ32024Global'),
    courseraPlusEpicClient.get('regionalPromotionQ32024Mexico'),
    courseraPlusEpicClient.get('regionalPromotionQ32024Brazil'),
    courseraPlusEpicClient.get('regionalPromotionQ32024India'),
    courseraPlusEpicClient.get('regionalPromotionQ32024Colombia'),
  ];

  const shouldRender = !!epicValues.find((val) => val !== 'control');

  if (hideGlobalPromotion && courseraPlusEpicClient.get('regionalPromotionQ32024Global') !== 'control') {
    return false;
  }

  return shouldRender;
};

const RegionalPromotionBanner = ({ hideGlobalPromotion, variant }: Props) => {
  // This should render if any treatment is applied to a user, XDP controls the flow to only treatment-b
  const getBannerContent = () => {
    if (!hideGlobalPromotion && courseraPlusEpicClient.get('regionalPromotionQ32024Global') !== 'control') {
      return (
        <>
          <Typography2 component="h2" variant="subtitleLarge">
            {_t(`Limited time only! Get Coursera Plus for 30% off.`)}
          </Typography2>
          <Typography2 component="p" variant="bodyPrimary">
            <FormattedHTMLMessage
              message={_t(`Reset. Reinvent. Reach new career goals. {claim}`)}
              claim={
                <a css={styles.link} href="https://www.coursera.org/courseraplus/special/global-thirty-2024">
                  {_t(`Claim your offer now.`)}
                </a>
              }
            />
          </Typography2>
        </>
      );
    }
    if (courseraPlusEpicClient.get('regionalPromotionQ32024Mexico') !== 'control') {
      return (
        <>
          <Typography2 component="h2" variant="subtitleLarge">
            <FormattedHTMLMessage
              message={_t(`Limited time only! Get Coursera Plus for {price} only.`)}
              price={<ReactPriceDisplay value={2199} currency={'MXN'} />}
            />
          </Typography2>
          <Typography2 component="p" variant="bodyPrimary">
            <FormattedHTMLMessage
              message={_t(`Reset. Reinvent. Reach new career goals. {claim}`)}
              claim={
                <a css={styles.link} href="https://www.coursera.org/courseraplus/special/coursera-plus-mxn-2024">
                  {_t(`Claim your offer now.`)}
                </a>
              }
            />
          </Typography2>
        </>
      );
    }
    if (courseraPlusEpicClient.get('regionalPromotionQ32024Brazil') !== 'control') {
      return (
        <>
          <Typography2 component="h2" variant="subtitleLarge">
            {_t(`Limited time only! Get Coursera Plus for 68% off.`)}
          </Typography2>
          <Typography2 component="p" variant="bodyPrimary">
            <FormattedHTMLMessage
              message={_t(`Reset. Reinvent. Reach new career goals. {claim}`)}
              claim={
                <a css={styles.link} href="https://www.coursera.org/courseraplus/special/coursera-plus-br-2024">
                  {_t(`Claim your offer now.`)}
                </a>
              }
            />
          </Typography2>
        </>
      );
    }
    if (courseraPlusEpicClient.get('regionalPromotionQ32024India') !== 'control') {
      return (
        <>
          <Typography2 component="h2" variant="subtitleLarge">
            <FormattedHTMLMessage
              message={_t(`Limited time only! Get Coursera Plus for {price} only.`)}
              price={<ReactPriceDisplay value={8499} currency={'INR'} />}
            />
          </Typography2>
          <Typography2 component="p" variant="bodyPrimary">
            <FormattedHTMLMessage
              message={_t(`Reset. Reinvent. Reach new career goals. {claim}`)}
              claim={
                <a css={styles.link} href="https://www.coursera.org/courseraplus/special/india-cplus-promo">
                  {_t(`Claim your offer now.`)}
                </a>
              }
            />
          </Typography2>
        </>
      );
    }
    if (courseraPlusEpicClient.get('regionalPromotionQ32024Colombia') !== 'control') {
      return (
        <>
          <Typography2 component="h2" variant="subtitleLarge">
            {_t(`Limited time only! Get Coursera Plus for 70% off.`)}
          </Typography2>
          <Typography2 component="p" variant="bodyPrimary">
            <FormattedHTMLMessage
              message={_t(`Reset. Reinvent. Reach new career goals. {claim}`)}
              claim={
                <a css={styles.link} href="https://www.coursera.org/courseraplus/special/coursera-plus-co-2024">
                  {_t(`Claim your offer now.`)}
                </a>
              }
            />
          </Typography2>
        </>
      );
    }

    return null;
  };

  const bannerContent = getBannerContent();

  if (!bannerContent) {
    return null;
  }

  // This div wrapper is required.
  // This wrapper prevents some sort of hydration related bug which leaks into the hero section below.
  return (
    <div>
      <RegionalPromotionFullWidthBanner variant={variant}>{getBannerContent()}</RegionalPromotionFullWidthBanner>
    </div>
  );
};

export { RegionalPromotionBanner };

export default RegionalPromotionBanner;
